import { useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { PillField } from '../field';
import { fieldErrorId } from '../fieldIds';
import {
  ICON_CLASS,
  PILL_CLASS,
  PILL_ERROR_CLASS,
  sizingClass,
  type Sizing,
} from '../tokens';

import { EmojiPickerPopover } from './EmojiPickerPopover';
import {
  phonePopoverPlacement,
  phoneScreenOf,
  type PopoverPlacement,
} from './emojiPopoverPlacement';
import { useCardMetrics } from './useCardMetrics';

const YOURS_CLASS = 'border-kind-act bg-kind-act/[.08]';
// A phone pill stays 32 units tall and 28 or more wide; an invisible box around it
// takes a 44-unit tap, as TaskRow's check does
const PHONE_HIT_CLASS =
  'relative before:absolute before:inset-y-[calc(-6*var(--ac-u))]';
const PHONE_ADD_HIT_CLASS = `${PHONE_HIT_CLASS} before:inset-x-[calc(-8*var(--ac-u))]`;

export type ChosenEmoji = { value: string; glyph: string };

// Slack's reactions row: one "yours" pill per chosen emoji, in pick order, then
// a fixed-size add pill that opens the picker. Clicking a pill takes it back
export function ReactionPills({
  id,
  chosen,
  count,
  limit,
  error,
  sizing,
  showAdd,
  interactive,
  onToggle,
}: {
  id: string;
  chosen: ChosenEmoji[];
  count: number | undefined;
  limit: number;
  error: string | undefined;
  sizing: Sizing | undefined;
  // The add pill stays while the card can still change, so a held run moves nothing
  showAdd: boolean;
  interactive: boolean;
  onToggle: (glyph: string) => void;
}) {
  const metrics = useCardMetrics();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [placement, setPlacement] = useState<PopoverPlacement>();
  const addRef = useRef<HTMLButtonElement>(null);
  const pillClass = cn(PILL_CLASS, YOURS_CLASS);
  const openPicker = (open: boolean) => {
    const add = addRef.current;
    if (open && metrics.mobile && add) {
      setPlacement(
        phonePopoverPlacement(add.getBoundingClientRect(), phoneScreenOf(add))
      );
    }
    setPickerOpen(open);
  };
  const content = (glyph: string) => (
    <>
      <span
        aria-hidden="true"
        className="inline-flex items-center leading-none"
      >
        {glyph}
      </span>
      {count !== undefined ? (
        <span
          aria-hidden="true"
          className="inline-flex items-center leading-none"
        >
          {count}
        </span>
      ) : null}
    </>
  );

  return (
    <XStack
      wrap
      role="group"
      aria-label="Reactions"
      id={showAdd ? undefined : id}
      className={cn(
        'min-w-0 items-center gap-y-[var(--ac-gap-xs)]',
        // A wider gap on a phone keeps neighbouring tap boxes apart
        metrics.mobile
          ? 'gap-x-[var(--ac-gap-sm)]'
          : 'gap-x-[calc(6*var(--ac-u))]',
        sizingClass(sizing)
      )}
    >
      {chosen.map(({ value, glyph }) =>
        interactive ? (
          <button
            key={value}
            type="button"
            aria-label={`Remove ${glyph}`}
            className={cn(pillClass, metrics.mobile && PHONE_HIT_CLASS)}
            onClick={() => onToggle(glyph)}
          >
            {content(glyph)}
          </button>
        ) : (
          <span
            key={value}
            role="img"
            aria-label={glyph}
            className={cn(pillClass, 'hover:bg-kind-act/[.08]')}
          >
            {content(glyph)}
          </span>
        )
      )}
      {showAdd ? (
        <PillField id={id} error={error} className="shrink-0">
          <EmojiPickerPopover
            open={pickerOpen}
            onOpenChange={openPicker}
            placement={metrics.mobile ? placement : undefined}
            choice={{ chosen: chosen.map(({ glyph }) => glyph), limit }}
            onSelect={onToggle}
            trigger={
              <button
                ref={addRef}
                type="button"
                id={id}
                aria-label="Add reaction"
                title={error ?? 'Add reaction'}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? fieldErrorId(id) : undefined}
                disabled={!interactive}
                className={cn(
                  PILL_CLASS,
                  'w-[calc(28*var(--ac-u))] shrink-0 justify-center px-0',
                  metrics.mobile && PHONE_ADD_HIT_CLASS,
                  error
                    ? PILL_ERROR_CLASS
                    : 'text-foreground-muted hover:text-foreground'
                )}
              >
                <Icons.smilePlus aria-hidden="true" className={ICON_CLASS} />
              </button>
            }
          />
        </PillField>
      ) : null}
    </XStack>
  );
}
